import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type SubmitEvent,
  type ReactNode,
} from 'react'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import type {
  PaymentIntentResult,
  PaymentMethod,
  PaymentMethodResult,
  SetupIntentResult,
  StripeCardCvcElement,
  StripeCardCvcElementChangeEvent,
  StripeCardCvcElementOptions,
  StripeCardExpiryElement,
  StripeCardExpiryElementChangeEvent,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementChangeEvent,
  StripeCardNumberElementOptions,
  StripePaymentElementOptions,
  StripeElementStyleVariant,
} from '@stripe/stripe-js'
import { getCountries } from 'libphonenumber-js'
import { CreditCard, Link2, Loader2 } from 'lucide-react'
import { LoadingState } from '@/components/patterns/loading-state'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import LoadingButton from '@/components/ui/loading-button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Country } from '@/lib/enums/Country'
import {
  formatCardBrand,
  formatCardExpiry,
  formatCardLast4,
  isNonCardLink,
  SAVED_PAYMENT_METHOD_AUTHORIZATION_COPY,
} from '@/lib/payment-method-display'
import { ADMIN_FONT_FAMILY } from '@/lib/admin-font'
import type {
  CustomerSavedCard,
  StripePaymentMethodInput,
} from '@/lib/services/customers/customers.types'
import { cn, readProperty } from '@/lib/utils'

/** Stripe confirms the card's payment with the client secret of the intent the server prepared for it. */
type ConfirmCardPayment = (clientSecret: string) => Promise<PaymentIntentResult>

/** Stripe confirms saving the card with the client secret of the setup intent the server prepared for it. */
type ConfirmCardSetup = (clientSecret: string) => Promise<SetupIntentResult>

/** A card on file staff picked to pay with. */
export interface SavedCardDetails {
  savedPaymentMethodId: string
  /** Sent when the form asks staff to confirm (`requiredConfirmationLabel`). */
  requiredConfirmationAccepted?: boolean
  confirmPayment: ConfirmCardPayment
}

/** A card staff typed (or a sales demo's practice Visa) to pay with now. */
export interface NewCardPaymentDetails {
  paymentMethod: StripePaymentMethodInput
  /** Sent when the form asks staff to confirm (`requiredConfirmationLabel`). */
  requiredConfirmationAccepted?: boolean
  /** Sent when staff save the card, or the payment needs a saved card. */
  savePaymentMethod?: true
  confirmPayment: ConfirmCardPayment
}

/** A card staff typed into a setup form, to save. */
export interface NewCardSetupDetails {
  paymentMethod: PaymentMethod
  /** Sent when the form asks staff to confirm (`requiredConfirmationLabel`). */
  requiredConfirmationAccepted?: boolean
  /** Sent when staff save the card, or the form needs a saved card. */
  savePaymentMethod?: true
  confirmSetup: ConfirmCardSetup
}

/** What a payment form hands over when staff press its button. */
export type CardPaymentDetails = SavedCardDetails | NewCardPaymentDetails

/**
 * What a setup form hands over: the typed card to save. A card on file staff picked, when the form offers them,
 * confirms a payment, as on a payment form.
 */
export type CardSetupDetails = NewCardSetupDetails | CardPaymentDetails

/** A form that charges the card. */
export interface PaymentModeProps {
  confirmationMode?: 'payment'
  /** Settles once the card is handled; a rejection's message is shown as the form's error. */
  onSubmitCardDetails: (details: CardPaymentDetails) => Promise<void>
}

/** A form that saves the card, as a customer's card on file. */
export interface SetupModeProps {
  confirmationMode: 'setup'
  /** Settles once the card is handled; a rejection's message is shown as the form's error. */
  onSubmitCardDetails: (details: CardSetupDetails) => Promise<void>
}

/** Whether the form charges or saves the card, and what it hands over when staff press its button. */
export type CardEntryModeProps = PaymentModeProps | SetupModeProps

/** Card entry's readiness, as `StripeElements` reports it. */
export type CardEntryStage =
  | 'payment_element_reset'
  | 'payment_element_ready'
  | 'payment_element_not_required'

interface StripeElementsSharedProps {
  disabled: boolean
  isDarkMode: boolean
  onError: (message: string) => void
  submitButtonLabel?: string
  submittingButtonLabel?: string
  /** The version of what staff confirm; a new version asks them again. */
  requiredConfirmationVersion?: string | null
  /** What staff confirm before submitting; no confirmation without one. */
  requiredConfirmationLabel?: string | null
  requiredConfirmationContent?: ReactNode
  requiredConfirmationErrorMessage?: string
  canSavePaymentMethod?: boolean
  requireSavedPaymentMethod?: boolean
  savedPaymentMethods?: CustomerSavedCard[]
  isSavedPaymentMethodsLoading?: boolean
  onInitializationStage?: (stage: CardEntryStage) => void
  salesDemoMode?: boolean
}

export type StripeElementsProps = StripeElementsSharedProps & CardEntryModeProps

type CardElementFieldName = 'cardNumber' | 'cardExpiry' | 'cardCvc'

type CardFieldName = CardElementFieldName | 'postalCode'

type CardFieldErrors = Record<CardFieldName, string | null>

type CardFieldStatus = Record<CardElementFieldName, { complete: boolean; error: string | null }>

interface SetupFieldReadiness extends Record<CardElementFieldName, boolean> {
  cardEntryIdentity: string | null
}

/** The setup fields report ready one at a time; the expiry and security code hand over their element for focus. */
type SetupFieldReady =
  | [fieldName: 'cardNumber']
  | [fieldName: 'cardExpiry', element: StripeCardExpiryElement]
  | [fieldName: 'cardCvc', element: StripeCardCvcElement]

const regionDisplayNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null

const countryOptions = (getCountries?.() ?? [])
  .map((code) => ({
    id: code,
    name: regionDisplayNames?.of(code) ?? code,
  }))
  .filter((option) => option.name)
  .sort((left, right) => left.name.localeCompare(right.name))

const EMPTY_FIELD_ERRORS: CardFieldErrors = {
  cardNumber: null,
  cardExpiry: null,
  cardCvc: null,
  postalCode: null,
}

const UNITED_STATES_POSTAL_CODE_MAX_LENGTH = 5
const NEW_CARD_PAYMENT_METHOD_ID = 'new'
const DEMO_CARD_PAYMENT_METHOD: StripePaymentMethodInput = {
  id: 'pm_card_visa',
  type: 'card',
  card: {
    brand: 'visa',
    last4: '4242',
  },
}
const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: {
    type: 'tabs',
  },
  paymentMethodOrder: ['card'],
  wallets: {
    applePay: 'never',
    googlePay: 'never',
    link: 'never',
  },
  defaultValues: {
    billingDetails: {
      address: {
        country: Country.UnitedStates.Value,
      },
    },
  },
}

const INCOMPLETE_FIELD_MESSAGES: Record<CardElementFieldName, string> = {
  cardNumber: 'Your card number is incomplete.',
  cardExpiry: "Your card's expiration date is incomplete.",
  cardCvc: "Your card's security code is incomplete.",
}

function createEmptyCardFieldStatus(): CardFieldStatus {
  return {
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  }
}

function getCardElementOptions(isDarkMode: boolean): {
  cardNumber: StripeCardNumberElementOptions
  expiry: StripeCardExpiryElementOptions
  cvc: StripeCardCvcElementOptions
} {
  const base: StripeElementStyleVariant = {
    fontSize: '16px',
    color: isDarkMode ? '#f4f4f5' : '#414042',
    iconColor: isDarkMode ? '#d3d3d5' : '#5c5b5e',
    fontFamily: ADMIN_FONT_FAMILY,
    '::placeholder': {
      // Hosted fields cannot inherit our CSS tokens; mirror --placeholder-foreground.
      color: isDarkMode ? '#8e8891' : '#8e8993',
    },
  }

  return {
    cardNumber: {
      showIcon: true,
      iconStyle: 'solid',
      placeholder: '1234 1234 1234 1234',
      disableLink: true,
      style: {
        base,
        invalid: {
          color: isDarkMode ? '#ecbfc4' : '#933b47',
          iconColor: isDarkMode ? '#ecbfc4' : '#933b47',
        },
      },
    },
    expiry: {
      placeholder: 'MM / YY',
      style: {
        base,
        invalid: {
          color: isDarkMode ? '#ecbfc4' : '#933b47',
        },
      },
    },
    cvc: {
      placeholder: 'CVC',
      style: {
        base,
        invalid: {
          color: isDarkMode ? '#ecbfc4' : '#933b47',
        },
      },
    },
  }
}

function resolveErrorField(message: string): CardFieldName | null {
  const normalizedMessage = message?.toLowerCase() ?? ''

  if (!normalizedMessage) {
    return null
  }

  if (normalizedMessage.includes('card number')) {
    return 'cardNumber'
  }

  if (normalizedMessage.includes('expiration')) {
    return 'cardExpiry'
  }

  if (normalizedMessage.includes('security code') || normalizedMessage.includes('cvc')) {
    return 'cardCvc'
  }

  if (normalizedMessage.includes('zip') || normalizedMessage.includes('postal')) {
    return 'postalCode'
  }

  return null
}

interface CardFieldFrameProps {
  children: ReactNode
  errorMessage?: string | null
  id: string
  label: string
  trailingAdornment?: ReactNode
}

function CardFieldFrame({
  children,
  errorMessage = null,
  id,
  label,
  trailingAdornment = null,
}: CardFieldFrameProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div
        className={cn(
          'rounded-lg bg-white px-3.5 py-3 ring-1 ring-border/60 transition-[background-color,box-shadow] focus-within:bg-white focus-within:ring-2 focus-within:ring-ring/40 dark:bg-input/30 dark:ring-white/10 dark:focus-within:bg-input/40 dark:focus-within:ring-ring/50',
          errorMessage &&
            'ring-destructive/60 focus-within:ring-destructive/40 dark:ring-destructive/50 dark:focus-within:ring-destructive/40'
        )}
      >
        <div id={id} className="flex items-center gap-3">
          <div className="min-w-0 flex-1">{children}</div>
          {trailingAdornment ? (
            <span className="shrink-0 text-muted-foreground">{trailingAdornment}</span>
          ) : null}
        </div>
      </div>
      {errorMessage ? (
        <Typography variant="label" tone="destructive" role="alert">
          {errorMessage}
        </Typography>
      ) : null}
    </div>
  )
}

function SavedCardOption({ card }: { card: CustomerSavedCard }) {
  const expiry = formatCardExpiry(card)
  const last4 = formatCardLast4(card)
  const isLink = isNonCardLink(card)
  const MethodIcon = isLink ? Link2 : CreditCard

  return (
    <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">
          {formatCardBrand(card)}
          {last4 ? ` ending in ${last4}` : ''}
        </span>
        <Typography variant="label" tone="muted" className="block">
          {expiry ? `Expires ${expiry}` : isLink ? 'Saved in Link' : 'Saved card'}
          {card?.is_default ? ' - Default' : ''}
        </Typography>
      </span>
      <MethodIcon className="size-4 shrink-0 text-muted-foreground" />
    </span>
  )
}

export function SecureCardFormLoadingState() {
  return (
    <LoadingState
      className="col-start-1 row-start-1"
      label="Loading secure card form..."
      size="compact"
      surface="transparent"
    />
  )
}

export function StripeElements({
  disabled,
  isDarkMode,
  onError,
  submitButtonLabel,
  submittingButtonLabel = 'Processing...',
  requiredConfirmationVersion = null,
  requiredConfirmationLabel = null,
  requiredConfirmationContent = null,
  requiredConfirmationErrorMessage = 'Confirm permission before saving this card.',
  canSavePaymentMethod = false,
  requireSavedPaymentMethod = false,
  savedPaymentMethods = [],
  isSavedPaymentMethodsLoading = false,
  onInitializationStage = undefined,
  salesDemoMode = false,
  ...cardEntryMode
}: StripeElementsProps) {
  const confirmationMode = cardEntryMode.confirmationMode ?? 'payment'
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [billingCountry, setBillingCountry] = useState<string>(Country.UnitedStates.Value)
  const [fieldErrors, setFieldErrors] = useState(EMPTY_FIELD_ERRORS)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(
    NEW_CARD_PAYMENT_METHOD_ID
  )
  const [isUsingDemoCard, setIsUsingDemoCard] = useState(false)
  const [savePaymentMethod, setSavePaymentMethod] = useState(false)
  const [requiredConfirmationState, setRequiredConfirmationState] = useState<{
    version: string | null
    accepted: boolean
    error: string | null
  }>({
    version: requiredConfirmationVersion,
    accepted: false,
    error: null,
  })
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [readyCardEntryIdentity, setReadyCardEntryIdentity] = useState<string | null>(null)
  const [setupFieldReadiness, setSetupFieldReadiness] = useState<SetupFieldReadiness>({
    cardEntryIdentity: null,
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  })
  const newCardPaymentMethodId = useId()
  const savedPaymentMethodOptionIdPrefix = useId()
  const savePaymentMethodCheckboxId = useId()
  const requiredConfirmationCheckboxId = useId()
  const cardElementOptions = getCardElementOptions(isDarkMode)
  const cardExpiryElementRef = useRef<StripeCardExpiryElement | null>(null)
  const cardCvcElementRef = useRef<StripeCardCvcElement | null>(null)
  const postalCodeInputRef = useRef<HTMLInputElement>(null)
  const postalCodeValueRef = useRef('')
  const setupFutureUsageRef = useRef<'off_session' | 'on_session' | null>(null)
  const fieldStatusRef = useRef(createEmptyCardFieldStatus())
  const isUnitedStatesBillingCountry = billingCountry === Country.UnitedStates.Value
  const selectedSavedPaymentMethod =
    !canSavePaymentMethod || selectedPaymentMethodId === NEW_CARD_PAYMENT_METHOD_ID
      ? null
      : savedPaymentMethods.find((paymentMethod) => paymentMethod.id === selectedPaymentMethodId)
  const effectiveSelectedPaymentMethodId = selectedSavedPaymentMethod
    ? selectedPaymentMethodId
    : NEW_CARD_PAYMENT_METHOD_ID
  const isUsingSavedPaymentMethod = Boolean(selectedSavedPaymentMethod)
  const [cardEntryLifecycle, setCardEntryLifecycle] = useState({
    isUsingSavedPaymentMethod,
    generation: 0,
  })
  let cardEntryGeneration = cardEntryLifecycle.generation
  if (cardEntryLifecycle.isUsingSavedPaymentMethod !== isUsingSavedPaymentMethod) {
    if (!isUsingSavedPaymentMethod) {
      cardEntryGeneration += 1
    }
    setCardEntryLifecycle({ isUsingSavedPaymentMethod, generation: cardEntryGeneration })
  }
  const cardEntryIdentity = `${selectedPaymentMethodId}:${cardEntryGeneration}`
  const isPaymentElementReady = readyCardEntryIdentity === cardEntryIdentity
  const areSetupElementsReady =
    setupFieldReadiness.cardEntryIdentity === cardEntryIdentity &&
    setupFieldReadiness.cardNumber &&
    setupFieldReadiness.cardExpiry &&
    setupFieldReadiness.cardCvc
  const isNewCardEntryReady =
    confirmationMode === 'payment' ? isPaymentElementReady : areSetupElementsReady
  const isCardEntryReady = isUsingSavedPaymentMethod || isUsingDemoCard || isNewCardEntryReady
  const effectiveSavePaymentMethod =
    canSavePaymentMethod &&
    !isUsingSavedPaymentMethod &&
    (savePaymentMethod || requireSavedPaymentMethod)
  const shouldShowSavedPaymentMethods =
    canSavePaymentMethod && (isSavedPaymentMethodsLoading || savedPaymentMethods.length > 0)
  const requiresConfirmation = Boolean(requiredConfirmationLabel)
  const isCurrentRequiredConfirmation =
    requiredConfirmationState.version === requiredConfirmationVersion
  const requiredConfirmationAccepted = isCurrentRequiredConfirmation
    ? requiredConfirmationState.accepted
    : false
  const requiredConfirmationError = isCurrentRequiredConfirmation
    ? requiredConfirmationState.error
    : null
  const isRequiredConfirmationMissing = requiresConfirmation && !requiredConfirmationAccepted
  const countryLabel =
    countryOptions.find((option) => option.id === billingCountry)?.name ?? 'Country'

  const previousIsUsingSavedPaymentMethodRef = useRef(isUsingSavedPaymentMethod)

  useEffect(() => {
    const wasUsingSavedPaymentMethod = previousIsUsingSavedPaymentMethodRef.current
    previousIsUsingSavedPaymentMethodRef.current = isUsingSavedPaymentMethod

    if (
      wasUsingSavedPaymentMethod &&
      !isUsingSavedPaymentMethod &&
      selectedPaymentMethodId !== NEW_CARD_PAYMENT_METHOD_ID
    ) {
      onInitializationStage?.('payment_element_reset')
    }
  }, [isUsingSavedPaymentMethod, onInitializationStage, selectedPaymentMethodId])

  const reportedReadyCardEntryIdentityRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isNewCardEntryReady || isUsingSavedPaymentMethod || isUsingDemoCard) {
      return
    }

    if (reportedReadyCardEntryIdentityRef.current === cardEntryIdentity) {
      return
    }

    reportedReadyCardEntryIdentityRef.current = cardEntryIdentity
    onInitializationStage?.('payment_element_ready')
  }, [
    cardEntryIdentity,
    isNewCardEntryReady,
    isUsingDemoCard,
    isUsingSavedPaymentMethod,
    onInitializationStage,
  ])

  const handlePaymentElementReady = () => {
    setReadyCardEntryIdentity(cardEntryIdentity)
  }

  const handleSetupElementReady = (...[fieldName, element]: SetupFieldReady) => {
    if (fieldName === 'cardExpiry') {
      cardExpiryElementRef.current = element
    } else if (fieldName === 'cardCvc') {
      cardCvcElementRef.current = element
    }

    setSetupFieldReadiness((current) => ({
      ...(current.cardEntryIdentity === cardEntryIdentity
        ? current
        : {
            cardEntryIdentity,
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
          }),
      [fieldName]: true,
    }))
  }

  const handlePaymentMethodSelection = (value: string) => {
    const isSelectingNewCard = value === NEW_CARD_PAYMENT_METHOD_ID

    if (isSelectingNewCard) {
      setReadyCardEntryIdentity(null)
      setSetupFieldReadiness({
        cardEntryIdentity: null,
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      })
      reportedReadyCardEntryIdentityRef.current = null
      onInitializationStage?.('payment_element_reset')
    } else {
      onInitializationStage?.('payment_element_not_required')
    }

    setSelectedPaymentMethodId(value)
    setIsUsingDemoCard(false)
    setSubmitError(null)
    setFieldErrors(EMPTY_FIELD_ERRORS)
    fieldStatusRef.current = createEmptyCardFieldStatus()
    postalCodeValueRef.current = ''
    if (postalCodeInputRef.current) {
      postalCodeInputRef.current.value = ''
    }
    cardCvcElementRef.current?.clear?.()
    if (!isSelectingNewCard) {
      setSavePaymentMethod(false)
    }
  }

  const handleDemoCardSelection = (selected: boolean) => {
    setIsUsingDemoCard(selected)
    setSelectedPaymentMethodId(NEW_CARD_PAYMENT_METHOD_ID)
    setSubmitError(null)
    setFieldErrors(EMPTY_FIELD_ERRORS)
    setSavePaymentMethod(false)
    onInitializationStage?.(
      selected
        ? 'payment_element_not_required'
        : isNewCardEntryReady
          ? 'payment_element_ready'
          : 'payment_element_reset'
    )
  }

  const setFieldError = (fieldName: CardFieldName, nextError: string | null) => {
    setFieldErrors((current) =>
      current[fieldName] === nextError ? current : { ...current, [fieldName]: nextError }
    )
  }

  const updateCardFieldStatus = (
    fieldName: CardElementFieldName,
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent
  ) => {
    fieldStatusRef.current[fieldName] = {
      complete: Boolean(event?.complete),
      error: event?.error?.message ?? null,
    }

    setFieldError(fieldName, event?.error?.message ?? null)
    setSubmitError(null)
  }

  const handleCardNumberChange = (event: StripeCardNumberElementChangeEvent) => {
    const wasComplete = fieldStatusRef.current.cardNumber.complete
    updateCardFieldStatus('cardNumber', event)

    if (event?.complete && !wasComplete) {
      cardExpiryElementRef.current?.focus()
    }
  }

  const handleCardExpiryChange = (event: StripeCardExpiryElementChangeEvent) => {
    const wasComplete = fieldStatusRef.current.cardExpiry.complete
    updateCardFieldStatus('cardExpiry', event)

    if (event?.complete && !wasComplete) {
      cardCvcElementRef.current?.focus()
    }
  }

  const handleCardCvcChange = (event: StripeCardCvcElementChangeEvent) => {
    const wasComplete = fieldStatusRef.current.cardCvc.complete
    updateCardFieldStatus('cardCvc', event)

    if (!isUsingSavedPaymentMethod && event?.complete && !wasComplete) {
      postalCodeInputRef.current?.focus()
    }
  }

  const handlePostalCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    let nextPostalCode = event.target.value

    if (isUnitedStatesBillingCountry) {
      nextPostalCode = nextPostalCode.slice(0, UNITED_STATES_POSTAL_CODE_MAX_LENGTH)
      event.target.value = nextPostalCode
    }

    postalCodeValueRef.current = nextPostalCode

    if (fieldErrors.postalCode) {
      setFieldError('postalCode', null)
    }

    if (submitError) {
      setSubmitError(null)
    }
  }

  useEffect(() => {
    if (!isUnitedStatesBillingCountry) {
      return
    }

    const trimmedPostalCode = postalCodeValueRef.current.slice(
      0,
      UNITED_STATES_POSTAL_CODE_MAX_LENGTH
    )

    if (trimmedPostalCode === postalCodeValueRef.current) {
      return
    }

    postalCodeValueRef.current = trimmedPostalCode

    if (postalCodeInputRef.current) {
      postalCodeInputRef.current.value = trimmedPostalCode
    }
  }, [isUnitedStatesBillingCountry])

  const handleRequiredConfirmationChange = (checked: boolean | 'indeterminate') => {
    const isChecked = checked === true

    setRequiredConfirmationState({
      version: requiredConfirmationVersion,
      accepted: isChecked,
      error: null,
    })
    setSubmitError(null)
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (disabled) {
      return
    }

    if (!stripe || !elements) {
      onError('Payment form is still loading. Please try again.')
      return
    }

    if (isRequiredConfirmationMissing) {
      setRequiredConfirmationState({
        version: requiredConfirmationVersion,
        accepted: false,
        error: requiredConfirmationErrorMessage,
      })
      return
    }

    const fieldsToValidate: CardElementFieldName[] =
      isUsingSavedPaymentMethod || confirmationMode === 'payment'
        ? []
        : ['cardNumber', 'cardExpiry', 'cardCvc']
    const nextFieldErrors = fieldsToValidate.reduce<CardFieldErrors>(
      (carry, fieldName) => {
        const fieldStatus = fieldStatusRef.current[fieldName]

        return {
          ...carry,
          [fieldName]:
            fieldStatus.error ??
            (fieldStatus.complete ? null : INCOMPLETE_FIELD_MESSAGES[fieldName]),
        }
      },
      { ...EMPTY_FIELD_ERRORS }
    )

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setFieldErrors(nextFieldErrors)
      return
    }

    setFieldErrors(EMPTY_FIELD_ERRORS)
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      if (selectedSavedPaymentMethod) {
        await cardEntryMode.onSubmitCardDetails({
          savedPaymentMethodId: selectedSavedPaymentMethod.id,
          ...(requiresConfirmation ? { requiredConfirmationAccepted } : {}),
          confirmPayment: (clientSecret) =>
            stripe.confirmCardPayment(clientSecret, {
              payment_method: selectedSavedPaymentMethod.id,
              return_url: window.location.href,
            }),
        })

        return
      }

      if (isUsingDemoCard) {
        await cardEntryMode.onSubmitCardDetails({
          paymentMethod: DEMO_CARD_PAYMENT_METHOD,
          ...(requiresConfirmation ? { requiredConfirmationAccepted } : {}),
          ...(effectiveSavePaymentMethod ? { savePaymentMethod: true } : {}),
          confirmPayment: (clientSecret) =>
            stripe.confirmCardPayment(clientSecret, {
              payment_method: DEMO_CARD_PAYMENT_METHOD.id,
              return_url: window.location.href,
            }),
        })

        return
      }

      let paymentMethodResult: PaymentMethodResult

      if (confirmationMode === 'payment') {
        const nextSetupFutureUsage = requireSavedPaymentMethod
          ? 'off_session'
          : effectiveSavePaymentMethod
            ? 'on_session'
            : null

        if (setupFutureUsageRef.current !== nextSetupFutureUsage) {
          // Deferred Elements options must match the PaymentIntent created after card collection.
          await new Promise<void>((resolve) => {
            elements.on('update-end', resolve)
            elements.update({ setupFutureUsage: nextSetupFutureUsage })
          })
          setupFutureUsageRef.current = nextSetupFutureUsage
        }

        const { error: submitValidationError } = await elements.submit()

        if (submitValidationError) {
          setSubmitError(
            submitValidationError.message || 'Check the card details before charging this card.'
          )
          return
        }

        paymentMethodResult = await stripe.createPaymentMethod({
          elements,
        })
      } else {
        const cardNumberElement = elements.getElement(CardNumberElement)

        if (!cardNumberElement) {
          onError('Payment form is still loading. Please try again.')
          return
        }

        paymentMethodResult = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            address: {
              country: billingCountry,
              postal_code: postalCodeValueRef.current.trim() || undefined,
            },
          },
        })
      }

      const { paymentMethod, error: paymentMethodError } = paymentMethodResult

      if (paymentMethodError || !paymentMethod) {
        const nextError =
          paymentMethodError?.message || 'Check the card details before charging this card.'
        const errorField = resolveErrorField(nextError)

        if (confirmationMode !== 'payment' && errorField) {
          setFieldError(errorField, nextError)
          setSubmitError(null)
        } else {
          setSubmitError(nextError)
        }

        return
      }

      if (cardEntryMode.confirmationMode === 'setup') {
        await cardEntryMode.onSubmitCardDetails({
          paymentMethod,
          ...(requiresConfirmation ? { requiredConfirmationAccepted } : {}),
          ...(effectiveSavePaymentMethod ? { savePaymentMethod: true } : {}),
          confirmSetup: (clientSecret) =>
            stripe.confirmCardSetup(clientSecret, {
              payment_method: paymentMethod.id,
              return_url: window.location.href,
            }),
        })
      } else {
        await cardEntryMode.onSubmitCardDetails({
          paymentMethod,
          ...(requiresConfirmation ? { requiredConfirmationAccepted } : {}),
          ...(effectiveSavePaymentMethod ? { savePaymentMethod: true } : {}),
          confirmPayment: (clientSecret) =>
            stripe.confirmCardPayment(clientSecret, {
              payment_method: paymentMethod.id,
              return_url: window.location.href,
            }),
        })
      }
    } catch (error) {
      const message = readProperty(error, 'message')
      onError(
        (typeof message === 'string' && message) ||
          'We were unable to process your payment. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pt-1">
      <div className={disabled ? 'pointer-events-none space-y-5 opacity-60' : 'space-y-5'}>
        {shouldShowSavedPaymentMethods ? (
          <div className="space-y-3 rounded-xl bg-muted/25 p-4 ring-1 ring-border/50 dark:bg-white/[0.03] dark:ring-white/8">
            <div className="flex items-center justify-between gap-3">
              <Label>Card on file</Label>
              {isSavedPaymentMethodsLoading ? (
                <Typography variant="label" tone="muted">
                  Loading...
                </Typography>
              ) : null}
            </div>
            <RadioGroup
              value={effectiveSelectedPaymentMethodId}
              onValueChange={handlePaymentMethodSelection}
              spacing="compact"
              disabled={disabled}
            >
              <div className="flex items-center gap-3 rounded-md border bg-background/70 p-3 text-sm transition-colors hover:bg-muted/30">
                <RadioGroupItem id={newCardPaymentMethodId} value={NEW_CARD_PAYMENT_METHOD_ID} />
                <Label
                  htmlFor={newCardPaymentMethodId}
                  className="min-w-0 flex-1 cursor-pointer font-medium"
                >
                  Use a new card
                </Label>
              </div>
              {savedPaymentMethods.map((paymentMethod) => {
                const optionId = `${savedPaymentMethodOptionIdPrefix}-${paymentMethod.id}`

                return (
                  <div
                    key={paymentMethod.id}
                    className="flex items-center gap-3 rounded-md border bg-background/70 p-3 transition-colors hover:bg-muted/30"
                  >
                    <RadioGroupItem id={optionId} value={paymentMethod.id} />
                    <Label htmlFor={optionId} className="min-w-0 flex-1 cursor-pointer">
                      <SavedCardOption card={paymentMethod} />
                    </Label>
                  </div>
                )
              })}
            </RadioGroup>
          </div>
        ) : null}

        {!isUsingSavedPaymentMethod ? (
          <div className="space-y-5">
            {salesDemoMode && confirmationMode === 'payment' ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-background/75 p-3.5 dark:bg-background/35">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accentSecondary text-accentSecondary-foreground"
                  >
                    <CreditCard className="size-4" />
                  </span>
                  <div className="min-w-0">
                    <Typography variant="labelStrong" className="block">
                      {isUsingDemoCard ? 'Demo Visa ending in 4242' : 'Want to skip card entry?'}
                    </Typography>
                    <Typography variant="label" tone="muted" className="block">
                      {isUsingDemoCard
                        ? 'This practice payment will be approved. No real money is processed.'
                        : 'Use a practice Visa that will be approved automatically.'}
                    </Typography>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={isUsingDemoCard ? 'ghostMuted' : 'outline'}
                  onClick={() => handleDemoCardSelection(!isUsingDemoCard)}
                  disabled={disabled}
                >
                  {isUsingDemoCard ? 'Enter card instead' : 'Use demo card'}
                </Button>
              </div>
            ) : null}

            <div
              hidden={isUsingDemoCard}
              className="space-y-5 rounded-xl bg-muted/25 p-4 ring-1 ring-border/50 dark:bg-white/[0.03] dark:ring-white/8"
            >
              {confirmationMode === 'payment' ? (
                <div className="grid min-h-24">
                  <div
                    className={cn('col-start-1 row-start-1', !isNewCardEntryReady && 'invisible')}
                    aria-hidden={!isNewCardEntryReady}
                  >
                    <PaymentElement
                      id="payment-element"
                      options={PAYMENT_ELEMENT_OPTIONS}
                      onReady={handlePaymentElementReady}
                      onChange={(event) => {
                        // Stripe.js types this event without an `error`; show one if Stripe sends it.
                        const errorMessage = readProperty(readProperty(event, 'error'), 'message')

                        if (typeof errorMessage === 'string' && errorMessage) {
                          setSubmitError(errorMessage)
                        } else if (submitError) {
                          setSubmitError(null)
                        }
                      }}
                    />
                  </div>
                  {!isNewCardEntryReady ? <SecureCardFormLoadingState /> : null}
                </div>
              ) : (
                <div className="grid min-h-24">
                  <div
                    className={cn(
                      'col-start-1 row-start-1 space-y-5',
                      !isNewCardEntryReady && 'invisible'
                    )}
                    aria-hidden={!isNewCardEntryReady}
                  >
                    <CardFieldFrame
                      id="card-number-element"
                      label="Card Number"
                      errorMessage={fieldErrors.cardNumber}
                    >
                      <CardNumberElement
                        options={cardElementOptions.cardNumber}
                        onReady={() => handleSetupElementReady('cardNumber')}
                        onChange={handleCardNumberChange}
                      />
                    </CardFieldFrame>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <CardFieldFrame
                        id="card-expiry-element"
                        label="Expiration Date"
                        errorMessage={fieldErrors.cardExpiry}
                      >
                        <CardExpiryElement
                          options={cardElementOptions.expiry}
                          onChange={handleCardExpiryChange}
                          onReady={(element) => handleSetupElementReady('cardExpiry', element)}
                        />
                      </CardFieldFrame>

                      <CardFieldFrame
                        id="card-cvc-element"
                        label="Security Code"
                        errorMessage={fieldErrors.cardCvc}
                        trailingAdornment={<CreditCard className="size-4" />}
                      >
                        <CardCvcElement
                          options={cardElementOptions.cvc}
                          onChange={handleCardCvcChange}
                          onReady={(element) => handleSetupElementReady('cardCvc', element)}
                        />
                      </CardFieldFrame>
                    </div>
                  </div>
                  {!isNewCardEntryReady ? <SecureCardFormLoadingState /> : null}
                </div>
              )}

              {confirmationMode !== 'payment' && isNewCardEntryReady ? (
                <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_180px]">
                  <div className="space-y-2">
                    <Label>Billing Country</Label>
                    <Select
                      value={billingCountry}
                      onValueChange={setBillingCountry}
                      disabled={disabled}
                    >
                      <SelectTrigger
                        aria-label="Billing Country"
                        className="w-full bg-white text-foreground shadow-none ring-1 ring-border/60 transition-[background-color,box-shadow] data-[size=default]:h-11 dark:bg-input/30 dark:ring-white/10"
                      >
                        <SelectValue placeholder="Select a country">{countryLabel}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {countryOptions.map((option) => (
                          <SelectItem key={option.id} value={option.id}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="card-postal-code">ZIP / Postal Code</Label>
                    <Input
                      id="card-postal-code"
                      ref={postalCodeInputRef}
                      onChange={handlePostalCodeChange}
                      placeholder="Optional"
                      maxLength={
                        isUnitedStatesBillingCountry
                          ? UNITED_STATES_POSTAL_CODE_MAX_LENGTH
                          : undefined
                      }
                      disabled={disabled}
                      className={cn(
                        'h-11 bg-white shadow-none ring-1 ring-border/60 dark:bg-input/30 dark:ring-white/10',
                        fieldErrors.postalCode &&
                          'ring-destructive/60 focus-visible:ring-destructive/40'
                      )}
                    />
                    {fieldErrors.postalCode ? (
                      <Typography variant="label" tone="destructive" role="alert">
                        {fieldErrors.postalCode}
                      </Typography>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            {canSavePaymentMethod && (isNewCardEntryReady || isUsingDemoCard) ? (
              <div className="grid grid-cols-[1rem_minmax(0,1fr)] items-center gap-3.5 rounded-lg border bg-background/75 px-3.5 py-3 text-sm transition-colors hover:bg-muted/30 dark:bg-background/35 dark:hover:bg-white/[0.04]">
                <Checkbox
                  id={savePaymentMethodCheckboxId}
                  checked={effectiveSavePaymentMethod}
                  onCheckedChange={(checked) => setSavePaymentMethod(checked === true)}
                  disabled={disabled || requireSavedPaymentMethod}
                  className="justify-self-center"
                />
                <Label
                  htmlFor={savePaymentMethodCheckboxId}
                  className="min-w-0 flex-1 cursor-pointer flex-col items-start gap-1.5 leading-normal"
                >
                  <span className="block text-sm font-semibold leading-5">
                    {requireSavedPaymentMethod
                      ? 'Save this card for this subscription'
                      : 'Save this card for future payments'}
                  </span>
                  <Typography
                    variant="label"
                    tone="muted"
                    weight="normal"
                    className="block max-w-[36rem]"
                  >
                    {requireSavedPaymentMethod
                      ? 'This card will be used for future subscription orders.'
                      : SAVED_PAYMENT_METHOD_AUTHORIZATION_COPY}
                  </Typography>
                </Label>
              </div>
            ) : null}
          </div>
        ) : null}

        {requiresConfirmation ? (
          <div className="space-y-2">
            <div
              className={cn(
                'overflow-hidden rounded-lg border bg-background text-left',
                requiredConfirmationError && 'border-destructive/50'
              )}
            >
              {requiredConfirmationContent ? (
                <ScrollArea
                  role="region"
                  aria-label="Subscription terms"
                  type="always"
                  className="max-h-64 overflow-hidden"
                  viewportClassName="max-h-64 overscroll-y-contain"
                >
                  <div className="px-4 py-3">{requiredConfirmationContent}</div>
                </ScrollArea>
              ) : null}
              <Label
                htmlFor={requiredConfirmationCheckboxId}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 px-4 py-3 leading-normal transition-colors hover:bg-muted/30 dark:hover:bg-white/[0.04]',
                  requiredConfirmationContent && 'border-t',
                  requiredConfirmationError &&
                    'border-status-destructive-border bg-status-destructive-background',
                  disabled && 'cursor-not-allowed'
                )}
              >
                <Checkbox
                  id={requiredConfirmationCheckboxId}
                  checked={requiredConfirmationAccepted}
                  onCheckedChange={handleRequiredConfirmationChange}
                  disabled={disabled}
                  aria-required="true"
                  aria-invalid={requiredConfirmationError ? 'true' : undefined}
                  className="shrink-0"
                />
                <Typography variant="label" className="block min-w-0 flex-1">
                  {requiredConfirmationLabel}
                </Typography>
              </Label>
            </div>
            {requiredConfirmationError ? (
              <Typography variant="label" tone="destructive" role="alert">
                {requiredConfirmationError}
              </Typography>
            ) : null}
          </div>
        ) : null}
      </div>

      {submitError ? (
        <Typography variant="label" tone="destructive" role="alert">
          {submitError}
        </Typography>
      ) : null}

      <div className="pt-1">
        <LoadingButton
          type="submit"
          isPending={isSubmitting}
          disabled={!stripe || !isCardEntryReady || disabled || isRequiredConfirmationMissing}
          className="w-full"
        >
          {isSubmitting ? submittingButtonLabel : (submitButtonLabel ?? 'Pay Now')}
        </LoadingButton>
      </div>
    </form>
  )
}
